// docs/25 D3: stashes on the board. A chest and the leader's war chest show from the start; a
// hidden cache only once it has been searched out (docs/52 HT1: the party's view does not carry it
// until then; the layer still skips `hidden` for an offline snapshot that does). An opened stash stays where it was, open and emptied, dimmed so the eye
// passes over it. Frames are the Oryx world sheet's chests (picks.json `chest_*`, `crate`).
import Phaser from 'phaser';
import type { StashView } from '../../core/sim/types';
import { findLookOf } from '../../core/explore/explore';
import { atlasForFrame } from '../assets';

/** `[closed, opened]` world frames per stash kind. */
export const STASH_FRAMES: Record<StashView['kind'], readonly [string, string]> = {
  Chest: ['world_0261', 'world_0264'],      // chest_closed, chest_open_empty
  WarChest: ['world_0325', 'world_0326'],   // chest_gold_closed, chest_gold_open
  Cache: ['world_0435', 'world_0264'],      // crate, then an emptied chest
  Pouch: ['item_072', 'item_072'],          // docs/126: item_coin_bag, gone once taken
  Find: ['item_072', 'item_072'],           // docs/138 XO7: drawn by its look (`findLookOf`), gone once taken
};

/** Gold glint under an unopened stash so it reads as loot on any ground. */
const GLINT = 0xffd166;

export class StashLayer {
  private objects: Phaser.GameObjects.GameObject[] = [];
  private stashes: readonly StashView[] = [];

  constructor(private readonly scene: Phaser.Scene, private readonly layer: Phaser.GameObjects.Layer, private readonly cell: number) {}

  /** A stash opens as the pulse plays (the next snapshot redraws it the same). */
  open(id: number): void {
    if (!this.stashes.some((s) => s.id === id && !s.opened)) return;
    this.draw(this.stashes.map((s) => (s.id === id ? { ...s, opened: true } : s)));
  }

  /**
   * docs/138 XO7: a find shown on the ground — a small gold glint and twinkle under it, and the thing
   * itself by its look (a purse, a ring, a trinket, a piece of gear; coins are drawn), at half a
   * square so it reads as something small lying there.
   */
  private find(f: StashView): void {
    const c = this.cell;
    const x = f.tile.x * c, y = f.tile.y * c;
    const g = this.scene.add.graphics();
    g.fillStyle(GLINT, 0.22).fillEllipse(x + c / 2, y + c * 0.72, c * 0.7, c * 0.28);
    this.layer.add(g);
    this.objects.push(g);
    // a glint at its upper right: a tapered four-point star (thin diamonds, so it never reads as a plus)
    const sx = x + c * 0.78, sy = y + c * 0.3, r = c * 0.17, t = c * 0.035;
    g.fillStyle(0xffe7a3, 0.95);
    g.fillTriangle(sx, sy - r, sx - t, sy, sx + t, sy).fillTriangle(sx, sy + r, sx - t, sy, sx + t, sy);
    g.fillTriangle(sx - r, sy, sx, sy - t, sx, sy + t).fillTriangle(sx + r, sy, sx, sy - t, sx, sy + t);
    g.fillStyle(0xffffff, 1).fillCircle(sx, sy, t * 1.2);
    const look = findLookOf(f.look);
    const atlas = look.frame ? atlasForFrame(look.frame) : '';
    if (look.frame && this.scene.textures.exists(atlas) && this.scene.textures.get(atlas).has(look.frame)) {
      const img = this.scene.add.image(x + c / 2, y + c * 0.6, atlas, look.frame).setOrigin(0.5, 0.5);
      img.setScale((c * 0.62) / Math.max(1, img.width));
      if (look.tint !== undefined) img.setTint(look.tint);
      this.layer.add(img);
      this.objects.push(img);
      return;
    }
    // coins (and the dev atlas): a little stack of gold discs
    const k = this.scene.add.graphics();
    for (const [dx, dy] of [[-0.12, 0.1], [0.1, 0.12], [0, 0.0]] as const) {
      const cx = x + c * (0.5 + dx), cy = y + c * (0.6 + dy);
      k.fillStyle(0x8a5a10, 1).fillEllipse(cx, cy + 1, c * 0.24, c * 0.13);
      k.fillStyle(0xffd166, 1).fillEllipse(cx, cy, c * 0.24, c * 0.13);
      k.fillStyle(0xfff0b0, 1).fillEllipse(cx - c * 0.03, cy - 0.5, c * 0.08, c * 0.04);
    }
    this.layer.add(k);
    this.objects.push(k);
  }

  draw(stashes: readonly StashView[] | undefined): void {
    for (const o of this.objects) o.destroy();
    this.objects = [];
    this.stashes = stashes ?? [];
    const c = this.cell;
    for (const f of this.stashes) {
      if (f.hidden || ((f.kind === 'Pouch' || f.kind === 'Find') && f.opened)) continue;
      if (f.kind === 'Find') { this.find(f); continue; }
      const [closed, open] = STASH_FRAMES[f.kind];
      const atlas = closed.startsWith('item_') ? 'items' : 'world';
      const x = f.tile.x * c, y = f.tile.y * c;
      if (!f.opened) {
        const g = this.scene.add.graphics();
        g.fillStyle(GLINT, f.kind === 'WarChest' ? 0.3 : 0.18);
        g.fillEllipse(x + c / 2, y + c * 0.78, c * 0.86, c * 0.34);
        this.layer.add(g);
        this.objects.push(g);
      }
      if (!this.scene.textures.exists(atlas) || !this.scene.textures.get(atlas).has(closed)) {
        // dev atlas: a vector box
        const g = this.scene.add.graphics();
        g.fillStyle(f.kind === 'WarChest' ? 0xc9a227 : 0x8a5a2b, f.opened ? 0.45 : 1);
        g.fillRect(x + c * 0.2, y + c * 0.35, c * 0.6, c * 0.45);
        this.layer.add(g);
        this.objects.push(g);
        continue;
      }
      const img = this.scene.add.image(x, y, atlas, f.opened ? open : closed).setOrigin(0, 0);
      if (f.opened) img.setAlpha(0.6);
      this.layer.add(img);
      this.objects.push(img);
      if (!f.opened && f.kind === 'WarChest') {
        // the leader's chest wears a gold keyline so it stands apart from plain chests
        const g = this.scene.add.graphics();
        g.lineStyle(1, GLINT, 0.9);
        g.strokeRect(x + 1.5, y + 1.5, c - 3, c - 3);
        this.layer.add(g);
        this.objects.push(g);
      }
    }
  }
}
